import type { EnrollmentItem, SendSkip } from '@shared/flow/enrollment';

/**
 * U2 (buttonless autosaving editor) — the ONE client-visible send-eligibility
 * check, shared by `EnrollmentItemEditor`'s single "Send" button and (U4,
 * bead .4) the full-screen review's per-row/per-group sendability + "will
 * skip — <reason>" labels. A null return means the item is sendable from the
 * client's point of view; a non-null return is both the disabled-reason
 * `title` AND (in U4) the skip-reason label — one string, one place, so the
 * copy can never drift between the two surfaces.
 *
 * Deliberately does NOT duplicate every server-side mark-ready gate (e.g. the
 * warm-intro-connector-required check, `WarmIntroRequiredError`) — only the
 * checks the client can know without a round trip: is a play assigned, and is
 * a sender set. Anything else the server rejects still surfaces via the
 * inline 400 render (see `EnrollmentItemEditor`'s `sendError` state), it just
 * doesn't pre-disable the button.
 */
export function itemBlockReason(item: EnrollmentItem, sendReadOnly: boolean): string | null {
  if (sendReadOnly) return 'Enroll queue is read-only while impersonating';
  // terra-ohe.8 (F1/DBOS durable-send migration U6) — `queued` sits between
  // admission and the DBOS workflow actually claiming the row (`sending`);
  // it is exactly as locked/unsendable as `sending` from the client's point
  // of view, it just gets its own copy so a rep can tell "admitted, waiting
  // its turn" apart from "actively being sent right now".
  if (item.status === 'queued') return 'Queued…';
  if (item.status === 'sending') return 'Sending…';
  if (item.status === 'submitted') return 'Already sent';
  if (item.play_type_id === null) return 'Assign a play first';
  if (!item.sender || !item.sender.trim()) return 'Needs a sender';
  // Flow Contacts Bulk Add-to-Queue Scope 2, U3 (Decision 9) — a plays-general
  // send-time gate: the contact was already sent SOME play in the last 30
  // days (`has_recent_queue_send`, U1's shared signal) and the rep hasn't
  // explicitly unlocked this item (`recent_invite_override`). Slotting this
  // check into `itemBlockReason` — rather than forking a second derivation —
  // is what automatically removes the item from `deriveGroupSendability`'s
  // `sendableIds`/"Send N" count/summary line AND disables
  // `EnrollmentItemEditor`'s own per-row Send (:798) for free (R6/R8). Ordered
  // AFTER `no_sender` on purpose: a rep fixing a missing sender should see
  // that blocker first, not discover the flag only afterward.
  if (item.has_recent_queue_send && !item.recent_invite_override) return RECENT_SEND_BLOCK_LABEL;
  return null;
}

/**
 * Copy for the flagged-row gate above — REVIEW-THESE (plan Decision 9/11,
 * Spencer to confirm at QA). Shared with `SEND_SKIP_REASON_LABELS.recent_invite`
 * below so the client-derived block and the server's post-send skip reason
 * can never drift into two different strings for the same condition.
 */
export const RECENT_SEND_BLOCK_LABEL = 'Recent queue send (last 30 days)';

/**
 * U3 — distinguishes a "recent queue send" block from every other
 * `itemBlockReason` outcome so `PlayQueueCard`/`ContactQueueRow` can render
 * the dedicated red badge + "Send anyway" affordance instead of (or beside)
 * the generic "will skip — <reason>" text. Deliberately re-checks the two
 * underlying fields rather than string-comparing `itemBlockReason`'s return
 * value: `itemBlockReason` also short-circuits on `sendReadOnly`/`sending`/
 * `submitted`/no-play/no-sender, none of which are "this row is flagged" —
 * a lone flagged item still needs the affordance surfaced even when it is
 * ALSO blocked for another, higher-priority reason (e.g. no sender yet).
 */
export function isRecentSendFlagged(item: EnrollmentItem): boolean {
  return item.has_recent_queue_send && !item.recent_invite_override;
}

/**
 * `SendSkip.reason` (U3's server-side admission vocabulary) -> rep-facing
 * copy — U4 behavior spec 5's "server admission results are authoritative"
 * rule. The client-visible checks above (`no_play`/`no_sender`) already
 * produce their OWN copy string directly (no enum in between); this map
 * covers the FULL server vocabulary, including the checks the client can't
 * know without a round trip (`no_email`, `no_connector`, `already_sent`,
 * `sending`, `not_ready`, `not_found`) — coding-standards.md's "no client
 * re-derivation of skip reasons beyond mapping the shared enum to copy."
 */
export const SEND_SKIP_REASON_LABELS: Record<SendSkip['reason'], string> = {
  not_ready: 'Not ready',
  not_found: 'Not found',
  no_play: 'Assign a play first',
  no_sender: 'Needs a sender',
  no_email: 'Contact has no email',
  no_connector: 'Needs a connector',
  already_sent: 'Already sent',
  sending: 'Sending…',
  // Flow Contacts Bulk Add-to-Queue Scope 2, U2a: `SendSkip['reason']` gained
  // `recent_invite` (a NEW server admission reason, U2b) — this Record is
  // total, so the enum addition is a compile error without an entry here.
  // Shares `RECENT_SEND_BLOCK_LABEL` with the client-derived block above
  // (U3) so the two can never drift into different copy for the same
  // condition.
  recent_invite: RECENT_SEND_BLOCK_LABEL,
};
